import { useState, useRef } from "react";
import Seo from "../components/Seo";
import { API_ENDPOINTS, HEALTH_CHECK_CONFIG } from "../config";
import Toast from "../components/Toast";
import { motion, AnimatePresence } from "framer-motion";

type ServerStatus = "unknown" | "waking" | "ready" | "error";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [inquiryType, setInquiryType] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState<ServerStatus>("unknown");
  const [buttonMessage, setButtonMessage] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const healthCheckRef = useRef<AbortController | null>(null);

  const checkServerHealth = async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(API_ENDPOINTS.HEALTH_CHECK, {
        method: "GET",
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response.ok;
    } catch (err) {
      if ((err as Error).name === "AbortError") throw err;
      return false;
    }
  };

  const wakeUpServer = async (): Promise<boolean> => {
    const { MAX_RETRIES, RETRY_INTERVAL } = HEALTH_CHECK_CONFIG;
    for (let i = 0; i < MAX_RETRIES; i++) {
      if (healthCheckRef.current?.signal.aborted) return false;
      const isHealthy = await checkServerHealth();
      if (isHealthy) return true;
      await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL));
    }
    return false;
  };

  async function sendContactForm(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setToast(null);

    try {
      if (serverStatus !== "ready") {
        setButtonMessage("Waiting for server...");
        setServerStatus("waking");
        healthCheckRef.current = new AbortController();
        const isReady = await wakeUpServer();
        if (!isReady) {
          setServerStatus("error");
          throw new Error("Server is not responding. Please try again later.");
        }
        setServerStatus("ready");
      }

      setButtonMessage("Sending...");

      const res = await fetch(API_ENDPOINTS.CONTACT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, company, inquiryType, message }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to send message");
      }

      await res.json();
      setToast({
        type: "success",
        message: "✅ Message sent successfully! We'll respond within 24 hours.",
      });
      setName("");
      setEmail("");
      setCompany("");
      setInquiryType("");
      setMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred";
      setToast({
        type: "error",
        message: `❌ ${errorMessage}. Please email us directly at info@zeroharmai.com`,
      });
    } finally {
      setLoading(false);
      setButtonMessage("");
    }
  }

  const isFormValid = name && email && inquiryType && message;
  const isButtonDisabled = loading || !isFormValid;

  return (
    <>
      <Seo
        title="Contact Zero Harm AI | Agentic AI & AI Security Development"
        description="Talk to Zero Harm AI about building AI agents, MCP integrations, AI guardrails, red teaming, or agent security."
        socialDescription="Talk to Zero Harm AI about building and securing AI agents."
        path="/contact"
      />

      <div className="max-w-4xl mx-auto py-8 px-4">
        <motion.h1
          className="text-3xl md:text-4xl font-bold mb-3 text-center bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Build Secure AI Agents With Us
        </motion.h1>

        <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
          Tell us about the agent you want to build or the AI system you need to secure. We will
          align on scope, risk, and the most useful next step.
        </p>

        {/* Service Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg text-center shadow-xs hover:shadow-md transition">
            <div className="text-2xl mb-1">🤖</div>
            <h3 className="font-bold text-sm">AI Agent Development</h3>
            <p className="text-xs text-gray-600">Agents, RAG, MCP</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center shadow-xs hover:shadow-md transition">
            <div className="text-2xl mb-1">🛡️</div>
            <h3 className="font-bold text-sm">AI Security Development</h3>
            <p className="text-xs text-gray-600">Guardrails, Redaction, Red Teaming</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center shadow-xs hover:shadow-md transition">
            <div className="text-2xl mb-1">🔍</div>
            <h3 className="font-bold text-sm">Agent Security Assessment</h3>
            <p className="text-xs text-gray-600">Threats, Tools, Data</p>
          </div>
        </div>

        {/* Contact Form */}
        <form
          className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-lg"
          onSubmit={sendContactForm}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-bold mb-1 text-gray-700">Name *</label>
              <input
                type="text"
                placeholder="John Doe"
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden transition"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1 text-gray-700">Email *</label>
              <input
                type="email"
                placeholder="john@company.com"
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden transition"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-1 text-gray-700">Company</label>
            <input
              type="text"
              placeholder="Acme Inc."
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden transition"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="inquiryType" className="block text-sm font-bold mb-1 text-gray-700">
              What can we help you with? *
            </label>
            <select
              id="inquiryType"
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden transition"
              value={inquiryType}
              onChange={(e) => setInquiryType(e.target.value)}
              required
            >
              <option value="">Select a service...</option>
              <optgroup label="AI Agent Development">
                <option value="ai-agent-development">🤖 Custom AI Agent Development</option>
                <option value="workflow-automation">🧩 Multi-Agent / Workflow Automation</option>
                <option value="mcp-development">🔌 MCP Server &amp; Tool Integration</option>
                <option value="agent-architecture">🏗️ AI Agent Architecture Review</option>
              </optgroup>
              <optgroup label="AI Security Development">
                <option value="ai-guardrails">🛡️ AI Guardrails &amp; Prompt-Injection Defense</option>
                <option value="data-protection">🔒 PII &amp; Sensitive-Data Protection</option>
                <option value="ai-red-team">🧪 AI Red Teaming / Adversarial Testing</option>
                <option value="agent-security">🔍 Agent Security Assessment</option>
                <option value="mcp-security">🔐 Secure MCP Deployment Review</option>
              </optgroup>
              <optgroup label="Secure Agent Operations">
                <option value="ai-devsecops">⚙️ AI DevSecOps Implementation</option>
                <option value="production-readiness">📈 AI Production Readiness</option>
                <option value="observability">🔎 AI Observability and Reliability</option>
                <option value="ai-governance">✅ AI Governance Technical Readiness</option>
              </optgroup>
              <optgroup label="Consulting">
                <option value="fractional-cto">🏗️ Fractional CTO / Technical Advisory</option>
                <option value="general">💬 General Inquiry</option>
              </optgroup>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-1 text-gray-700">Project Details *</label>
            <textarea
              rows={5}
              placeholder="Tell us about your project:&#10;• What are you building?&#10;• Timeline and budget?&#10;• Any specific requirements?"
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden transition"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          <motion.button
            type="submit"
            className="w-full bg-linear-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all font-semibold min-h-[52px]"
            disabled={isButtonDisabled}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>{buttonMessage || "Processing..."}</span>
              </>
            ) : (
              <>Send Message <span className="text-lg">→</span></>
            )}
          </motion.button>

          {serverStatus === "waking" && (
            <p className="text-center text-sm text-gray-500 mt-3 flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Connecting to server...
            </p>
          )}
          {serverStatus === "ready" && (
            <p className="text-center text-sm text-green-600 mt-3">✓ Server connected</p>
          )}
          {serverStatus === "error" && (
            <p className="text-center text-sm text-amber-600 mt-3">
              ⚠ Server may be slow to respond. Your message will still be sent.
            </p>
          )}
        </form>

        {/* Info Grid */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-linear-to-br from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>📞</span> Contact Information
            </h3>
            <div className="space-y-2 text-gray-700 text-sm">
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:info@zeroharmai.com" className="text-blue-600 underline hover:text-blue-700">
                  info@zeroharmai.com
                </a>
              </p>
              <p><strong>Location:</strong> Portland, Oregon, USA</p>
              <p><strong>Response Time:</strong> Within 24 hours</p>
            </div>
          </div>

          <div className="bg-linear-to-br from-green-50 to-blue-50 p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>✅</span> What Happens Next?
            </h3>
            <ol className="text-gray-700 space-y-2 text-sm">
              <li><strong>1.</strong> We review your inquiry within 24 hours</li>
              <li><strong>2.</strong> Schedule a free 30-minute consultation</li>
              <li><strong>3.</strong> Receive detailed proposal in 3 business days</li>
              <li><strong>4.</strong> Kick off with discovery phase</li>
            </ol>
          </div>
        </div>

        <AnimatePresence>
          {toast && (
            <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
